import React from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { setDecimalPrecision } from 'utils/helpers'

const generateColumns = (useMinimalObt, isPackingList) => {
  const defaultColumns = [
    {
      headerName: useMinimalObt ? 'Material' : 'Material Name',
      field: 'materialName',
    },
    {
      headerName: '# Packages',
      field: 'numPackages',
    },
    {
      headerName: 'Package Type',
      field: 'packageType',
    },
    {
      headerName: 'Gross',
      field: 'gross',
    },
    {
      headerName: 'Tare',
      field: 'tare',
    },
    {
      headerName: 'Net',
      field: 'net',
    },
  ]

  // Remove the "# Packages" and "Package Type" columns if both of the following are true:
  // 1. printing Bill of Lading from OBT page
  // 2. useMinimalObt is turned on

  if (!isPackingList && useMinimalObt) {
    return defaultColumns.filter(column => column.field !== 'numPackages' && column.field !== 'packageType')
  }
  return defaultColumns
}

const MaterialsSummaryTable = ({ rows, totals, isPackingList }) => {
  const { useMinimalObt } = useSelector(state => state.featureFlags?.variables || {})

  const columns = React.useMemo(() => generateColumns(useMinimalObt, isPackingList), [useMinimalObt, isPackingList])

  return (
    <TableContainer>
      <Table
        size="small"
        aria-label="a simple materials table"
        sx={{
          td: {
            lineHeight: '0.6',
          },
        }}
      >
        <TableHead>
          <TableRow>
            {columns.map((column, index) => (
              <TableCell key={`headers-${column.field}`} align={index > 0 ? 'right' : 'left'}>
                {column.headerName}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.id}>
              {columns.map((column, columnIndex) => (
                <TableCell key={`fields-${column.field}`} align={columnIndex > 0 ? 'right' : 'left'}>
                  {column.field === 'materialName' || column.field === 'packageType'
                    ? row[column.field]
                    : setDecimalPrecision(row[column.field], { decimalPrecisionOverride: 0 })}
                </TableCell>
              ))}
            </TableRow>
          ))}

          <TableRow sx={{ 'td, th': { fontWeight: 'bold' } }}>
            <TableCell component="th" scope="row">
              TOTALS
            </TableCell>

            {columns
              .filter(column => column.field !== 'materialName')
              .map(column => (
                <TableCell key={`totals-${column.field}`} align="right">
                  {setDecimalPrecision(totals[column.field]?.value, { decimalPrecisionOverride: 0 })}
                </TableCell>
              ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  )
}

MaterialsSummaryTable.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      materialName: PropTypes.string.isRequired,
      gross: PropTypes.number.isRequired,
      tare: PropTypes.number.isRequired,
      net: PropTypes.number.isRequired,
      numPackages: PropTypes.number.isRequired,
      packageType: PropTypes.string,
    }),
  ),
  totals: PropTypes.shape({
    gross: PropTypes.object,
    net: PropTypes.object,
    numPackages: PropTypes.object,
    packageType: PropTypes.object,
    tare: PropTypes.object,
  }),
  isPackingList: PropTypes.bool,
}

MaterialsSummaryTable.defaultProps = {
  rows: [],
  totals: {},
  isPackingList: false,
}

export default MaterialsSummaryTable
